import { Component, OnInit } from '@angular/core';
import { first, interval } from 'rxjs';
import { InitService } from 'src/app/services/init.service';
import { SocketService } from 'src/app/services/socket.service';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'app-init',
    templateUrl: './init.component.html',
    styleUrls: ['./init.component.scss'],
    standalone: false
})
export class InitComponent implements OnInit {
  public nwis: string[] = [];
  public selectedNWI: string = null;
  public selectedProtocol: string = "http";
  public selectedPort: number = 22;
  public machineid: string = null;
  public initializing: boolean = false;
  public status: string = null;
  public initProgress: any[] = [
    { msg: 'Keycloak waiting for initialization', status: 'WAITING' },
    { msg: 'Host waiting for initialization', status: 'WAITING' },
    { msg: 'SSH waiting for initialization', status: 'WAITING' },
    { msg: 'Mux waiting for initialization', status: 'WAITING' },
    { msg: 'API waiting for initialization', status: 'WAITING' }
  ];

  constructor(
    private _initService: InitService,
    private _socketService: SocketService,
    private router: Router,
  ) {}

  async ngOnInit(): Promise<void> {
    (<any>window).init = this;

    const results: any = await this._initService.getNetworkInterfacesAsync();
    this.nwis = results.networkInterfaces;

    // Auto-select the best network interface (prefer public IPs, avoid localhost/private)
    this.selectedNWI = this.selectBestNetworkInterface(this.nwis);

    const results2: any = await this._initService.getMachineIdAsync();
    this.machineid = results2.machineid;
  }

  /**
   * Select the best network interface from the list.
   * Priority:
   * 1. Public IP (not starting with 127., 10., 172.16-31., 192.168.)
   * 2. Last non-localhost IP in the list
   * 3. Fallback to first available
   */
  private selectBestNetworkInterface(interfaces: string[]): string {
    if (!interfaces || interfaces.length === 0) return null;

    // Find a public IP first
    const publicIP = interfaces.find(ip => {
      if (ip.startsWith('127.')) return false;
      if (ip.startsWith('10.')) return false;
      if (ip.startsWith('192.168.')) return false;
      // 172.16.0.0 - 172.31.255.255
      if (ip.startsWith('172.')) {
        const secondOctet = parseInt(ip.split('.')[1], 10);
        if (secondOctet >= 16 && secondOctet <= 31) return false;
      }
      return true;
    });

    if (publicIP) return publicIP;

    // Otherwise pick the last non-localhost IP (usually the most recently added/external)
    const nonLocalhost = interfaces.filter(ip => !ip.startsWith('127.'));
    if (nonLocalhost.length > 0) {
      return nonLocalhost[nonLocalhost.length - 1];
    }

    // Fallback to first available
    return interfaces[0];
  }

  async assign() {
    this.initializing = true;

    // Start trial without license - license can be added later via LicenseWidget
    this._socketService.init(
      `${this.selectedProtocol}://${this.selectedNWI}`,
      this.selectedPort,
      "",  // No licensee for trial
      ""   // No signature for trial
    );
    this._socketService.initEvents.subscribe((msg: any) => {
      switch (msg) {
        case 'KEYCLOAK':
          this.initProgress[0] = {
            msg: 'Keycloak Initializing',
            status: 'INIT'
          };
          break;
        case 'HOST':
          this.initProgress[0] = {
            msg: 'Keycloak Initialized',
            status: 'COMPLETE'
          };
          this.initProgress[1] = {
            msg: 'Host Initializing',
            status: 'INIT'
          };
          break;
        case 'SSH':
          this.initProgress[1] = {
            msg: 'Host Initialized',
            status: 'COMPLETE'
          };
          this.initProgress[2] = {
            msg: 'SSH Initializing',
            status: 'INIT'
          };
          break;
        case 'MUX':
          this.initProgress[2] = {
            msg: 'SSH Initialized',
            status: 'COMPLETE'
          };
          this.initProgress[3] = {
            msg: 'Mux Initializing',
            status: 'INIT'
          };
          break;
        case 'API':
          this.initProgress[3] = {
            msg: 'Mux Initialized',
            status: 'COMPLETE'
          };
          this.initProgress[4] = {
            msg: 'API Initializing',
            status: 'INIT'
          };
          this.ping();
          break;
      }
    });
  }

  ping() {
    const intervalSeconds = 10;
    const source = interval(intervalSeconds * 1000);
    source.subscribe(() => {
      this._initService
        .ping()
        .pipe(first())
        .subscribe((resp: any) => {
          this.initProgress[4] = {
            msg: 'API Initialized.',
            status: 'COMPLETE'
          };

          let seconds = 10;
          const timer = setInterval(() => {
            this.status = `You will be redirected to the login page in ${seconds} seconds.`;
            seconds--;

            if (seconds == 0) {
              clearInterval(timer);
              window.location.href = "/";
            }
          }, 1000);
        });
    });
  }
}
